import { Company } from 'module/api/company/types';
import { useTranslate } from 'module/hooks/use-translation';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { Menu } from 'module/ui/components/atoms/dropdown/menu';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
  faWarning,
  faLightbulbOn,
  faWrench,
  faCalendar,
} from '@fortawesome/pro-solid-svg-icons';
import { Tag } from '@rocketlane/ui';
import { SignalConfiguration, SignalType } from 'api/signal/types';
import { SignalOccurrencesAPI } from 'api/signal-occurrences';
import { ReasonsResponse } from 'api/signal-occurrences/types';
import moment from 'moment';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  DateFilter,
  getStoredDateFilter,
  saveDateFilter,
  getDateRangeFromFilter,
  getFilterLabelKey,
} from './reasons-view-utils';
import { SignalTOC } from './signal-toc';
import {
  getSignalsV2CanCreateSignal,
  SignalsV2AccountEmptyState,
  type SignalsV2SignalPermissions,
} from './signals-v2-account-empty-state';
import { SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX } from './utils';

type FilterLabelTranslationKey =
  | 'accounts.page.signals_v2.reasons.filter.this_week'
  | 'accounts.page.signals_v2.reasons.filter.last_week'
  | 'accounts.page.signals_v2.reasons.filter.this_month'
  | 'accounts.page.signals_v2.reasons.filter.last_month'
  | 'accounts.page.signals_v2.reasons.filter.this_quarter'
  | 'accounts.page.signals_v2.reasons.filter.last_quarter'
  | 'accounts.page.signals_v2.reasons.filter.last_90_days';

const ReasonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  position: relative;
  justify-content: flex-start;
  align-self: flex-start;
  overflow-y: auto;
`;

const TocSidebar = styled.div`
  position: sticky;
  left: var(--spacing-04);
  padding: var(--spacing-04);
  z-index: 10;
  flex-shrink: 0;
`;

const ReasonsContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  justify-content: flex-start;
  align-items: stretch;
  padding: 0 var(--spacing-08);
  position: relative;
  gap: 0;
  margin-top: var(--spacing-06);
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  width: 100%;
  padding: var(--spacing-03) 0;
  position: sticky;
  top: 0;
  background-color: var(--background-primary);
  z-index: 5;
  margin-bottom: var(--spacing-07);
  box-shadow: 0px 5px 10px 0px #ffffff;
`;

const ReasonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  max-width: ${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px;
  margin-left: auto;
  margin-right: auto;
  gap: var(--spacing-08);
`;

const FilterDropdownTrigger = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: var(--spacing-03);
  margin-right: var(--spacing-06);
  border-radius: var(--border-radius-default);
  background-color: var(--gray-10);
  color: var(--text-primary);
  cursor: pointer;
  font-size: var(--font-size-base);
  font-weight: 600;
  line-height: var(--line-height-tag);
  border: 2px solid var(--gray-10);
  transition: all 0.2s ease;
  height: 32px;

  &:hover {
    background-color: var(--gray-20);
    border-color: var(--gray-20);
  }
`;

const FilterLabel = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const FilterDateRange = styled.span`
  color: var(--text-primary);
`;

const FilterIcon = styled(FontAwesomeIcon)`
  color: var(--icon-primary);
  font-size: var(--font-size-sm);
`;

const CalendarIcon = styled(FontAwesomeIcon)`
  color: var(--text-primary);
  font-size: var(--font-size-sm);
`;

const SignalSection = styled.div`
  padding-bottom: var(--spacing-08);
  border-bottom: 1px solid var(--gray-10);
`;

const SignalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  margin-bottom: var(--spacing-06);
`;

const SignalIcon = styled(FontAwesomeIcon)<{ $signalType: SignalType }>`
  color: ${({ $signalType }) => {
    switch ($signalType) {
      case SignalType.Risk:
        return 'var(--red-60)';
      case SignalType.Opportunity:
        return 'var(--green-60)';
      case SignalType.Operational:
        return 'var(--icon-secondary)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

const SignalName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const ReasonGroup = styled.div`
  margin-bottom: var(--spacing-07);

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReasonTag = styled(Tag)<{ $signalType: SignalType }>`
  margin-right: var(--spacing-02);

  && {
    background-color: ${({ $signalType }) => {
      switch ($signalType) {
        case SignalType.Opportunity:
          return 'var(--green-10)';
        case SignalType.Operational:
          return 'var(--gray-10)';
        case SignalType.Risk:
        default:
          return 'var(--red-10)';
      }
    }};
    color: ${({ $signalType }) => {
      switch ($signalType) {
        case SignalType.Opportunity:
          return 'var(--green-70)';
        case SignalType.Operational:
          return 'var(--gray-70)';
        case SignalType.Risk:
        default:
          return 'var(--red-70)';
      }
    }};
    border: none;
    font-weight: 500;
  }
`;

const ReasonList = styled.ul`
  list-style: none;
  list-style-type: none;
  padding: 0;
  padding-inline-start: 0;
  margin: 0;
  margin-top: var(--spacing-03);

  & li {
    list-style: none;
    list-style-type: none;
  }
`;

const ReasonItem = styled.li`
  list-style: none;
  list-style-type: none;
  padding-left: var(--spacing-05);
  padding-inline-start: var(--spacing-05);
  font-size: var(--font-size-base);
  line-height: 1.5;
  position: relative;
  display: block;

  &::before {
    content: '•';
    color: var(--text-secondary);
    font-weight: bold;
    position: absolute;
    left: var(--spacing-02);
  }

  &::marker {
    display: none;
    content: '';
  }
`;

const NoSignalFound = styled.div`
  color: var(--text-secondary);
  font-size: var(--font-size-base);
  padding: var(--spacing-03) 0;
  text-align: center;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

interface SignalsV2ReasonsViewProps {
  company: Company;
  signalPermissions?: SignalsV2SignalPermissions;
}

interface MappedReason {
  reasonId: string;
  reasonLabel: string;
  reasons: string[];
}

interface MappedSignal {
  signalConfigurationId: string;
  signalName: string;
  signalType: SignalType;
  reasonGroups: MappedReason[];
  /** True when this configuration defines a mappable Reason (or single-option) field. */
  canMapReasons: boolean;
}

export const SignalsV2ReasonsView: React.FC<SignalsV2ReasonsViewProps> = ({
  company,
  signalPermissions,
}) => {
  const translate = useTranslate();
  const { data: signalConfigsList = [], isLoading: isSignalConfigsLoading } =
    useGetAllSignalConfigurationsQuery();
  const hasNoSignalConfigurations =
    !isSignalConfigsLoading && signalConfigsList.length === 0;
  const canCreateSignal = getSignalsV2CanCreateSignal(signalPermissions);
  const [dateFilter, setDateFilter] = useState<DateFilter>(
    getStoredDateFilter(),
  );
  const [reasonsData, setReasonsData] = useState<ReasonsResponse | null>(null);
  const [selectedSignalId, setSelectedSignalId] = useState<string | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch reasons data
  useEffect(() => {
    const fetchReasons = async () => {
      setIsLoading(true);
      try {
        const dateRange = getDateRangeFromFilter(dateFilter);
        const reasons = await SignalOccurrencesAPI.getReasons(
          company.companyId,
          dateRange,
        );

        setReasonsData(reasons);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching reasons:', error as Error);
        setReasonsData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReasons();
  }, [dateFilter, company.companyId]);

  // Map all signal configs with their reasons; include every signal, empty reasonGroups if no occurrences
  const mappedSignals = useMemo<MappedSignal[]>(() => {
    if (signalConfigsList.length === 0) {
      return [];
    }

    const signals: MappedSignal[] = [];

    signalConfigsList.forEach((config) => {
      const signalConfigId = config.signalConfigurationId.toString();
      const reasonGroupsFromApi = reasonsData?.[signalConfigId] ?? {};

      // Find the Reason field in the config
      const reasonField = config.fields?.find(
        (field: any) =>
          field.fieldName === 'Reason' ||
          field.fieldLabel === 'Reason' ||
          (field.metaData?.options && config.fields?.length === 1),
      );

      let mappedReasonGroups: MappedReason[] = [];

      if (
        reasonField?.metaData?.options &&
        Object.keys(reasonGroupsFromApi).length > 0
      ) {
        mappedReasonGroups = Object.entries(reasonGroupsFromApi).map(
          ([reasonId, reasons]) => {
            const option = reasonField.metaData?.options?.find(
              (opt: any) => opt.value.toString() === reasonId,
            );
            const reasonLabel = option?.label || `Reason ${reasonId}`;

            return {
              reasonId,
              reasonLabel,
              reasons,
            };
          },
        );
      }

      const canMapReasons = Boolean(reasonField?.metaData?.options?.length);

      signals.push({
        signalConfigurationId: signalConfigId,
        signalName: config.signalName,
        signalType: config.signalType,
        reasonGroups: mappedReasonGroups,
        canMapReasons,
      });
    });

    const withReasons = signals.filter((s) => s.reasonGroups.length > 0);
    const withoutReasons = signals.filter((s) => s.reasonGroups.length === 0);

    return [...withReasons, ...withoutReasons];
  }, [reasonsData, signalConfigsList]);

  /** Same order as main content: signals with reasons first, empty at end; each signal once. */
  const signalTocConfigs = useMemo(() => {
    const configById = new Map(
      signalConfigsList.map((c) => [c.signalConfigurationId.toString(), c]),
    );

    return mappedSignals
      .map((s) => configById.get(s.signalConfigurationId))
      .filter((c): c is SignalConfiguration => c != null);
  }, [mappedSignals, signalConfigsList]);

  const handleFilterChange = (filter: DateFilter) => {
    setDateFilter(filter);
    saveDateFilter(filter);
  };

  const handleSignalSelect = (signalId: string) => {
    // Toggle selection: if same signal is clicked, deselect it
    if (selectedSignalId === signalId) {
      setSelectedSignalId(undefined);
    } else {
      setSelectedSignalId(signalId);
    }
  };

  // When a signal is selected, scroll so it appears 100px from top (below header)
  const SCROLL_TOP_OFFSET_PX = 100;

  useEffect(() => {
    if (!selectedSignalId || !scrollContainerRef.current) {
      return;
    }

    const element = document.getElementById(
      `signal-section-${selectedSignalId}`,
    );
    const container = scrollContainerRef.current;

    if (!element) {
      return;
    }

    const elementTop = element.getBoundingClientRect().top;
    const containerTop = container.getBoundingClientRect().top;
    const relativeTop = elementTop - containerTop;

    container.scrollTo({
      top: container.scrollTop + relativeTop - SCROLL_TOP_OFFSET_PX,
      behavior: 'smooth',
    });
  }, [selectedSignalId]);

  const currentDateRange = useMemo(() => {
    const range = getDateRangeFromFilter(dateFilter);
    const start = moment(range.startDate).format('D MMM YYYY');
    const end = moment(range.endDate).format('D MMM YYYY');

    return `${start} - ${end}`;
  }, [dateFilter]);

  const getSignalIcon = (signalType: SignalType) => {
    switch (signalType) {
      case SignalType.Risk:
        return faWarning;
      case SignalType.Opportunity:
        return faLightbulbOn;
      case SignalType.Operational:
        return faWrench;
      default:
        return faWarning;
    }
  };

  const filterMenuItems = useMemo(() => {
    return [
      {
        key: DateFilter.ThisWeek,
        label: translate('accounts.page.signals_v2.reasons.filter.this_week'),
      },
      {
        key: DateFilter.LastWeek,
        label: translate('accounts.page.signals_v2.reasons.filter.last_week'),
      },
      {
        key: DateFilter.ThisMonth,
        label: translate('accounts.page.signals_v2.reasons.filter.this_month'),
      },
      {
        key: DateFilter.LastMonth,
        label: translate('accounts.page.signals_v2.reasons.filter.last_month'),
      },
      {
        key: DateFilter.ThisQuarter,
        label: translate(
          'accounts.page.signals_v2.reasons.filter.this_quarter',
        ),
      },
      {
        key: DateFilter.LastQuarter,
        label: translate(
          'accounts.page.signals_v2.reasons.filter.last_quarter',
        ),
      },
      {
        key: DateFilter.Last90Days,
        label: translate(
          'accounts.page.signals_v2.reasons.filter.last_90_days',
        ),
      },
    ];
  }, [translate]);

  const menu = (
    <Menu
      selectedKeys={[dateFilter]}
      items={filterMenuItems}
      onClick={({ key }) => handleFilterChange(key as DateFilter)}
    />
  );

  const allSignalsHaveNoReasonData =
    !isLoading &&
    signalConfigsList.length > 0 &&
    mappedSignals.length > 0 &&
    mappedSignals.every((s) => s.reasonGroups.length === 0) &&
    mappedSignals.some((s) => s.canMapReasons);

  const hideTocForAccountEmpty =
    hasNoSignalConfigurations || allSignalsHaveNoReasonData;

  return (
    <>
      {signalTocConfigs.length > 0 && !hideTocForAccountEmpty && (
        <TocSidebar>
          <SignalTOC
            signalConfigs={signalTocConfigs}
            onSignalSelect={handleSignalSelect}
            selectedSignalId={selectedSignalId}
            title={translate('accounts.page.signals_v2.toc.signals')}
          />
        </TocSidebar>
      )}
      <ReasonsWrapper ref={scrollContainerRef}>
        {!hasNoSignalConfigurations && (
          <HeaderSection>
            <Dropdown trigger={['click']} overlay={menu}>
              <FilterDropdownTrigger>
                <CalendarIcon icon={faCalendar} />
                <FilterLabel>
                  {translate(
                    getFilterLabelKey(dateFilter) as FilterLabelTranslationKey,
                  )}
                </FilterLabel>
                <FilterDateRange>{currentDateRange}</FilterDateRange>
                <FilterIcon icon={faChevronDown} />
              </FilterDropdownTrigger>
            </Dropdown>
          </HeaderSection>
        )}
        <ReasonsContent>
          <ReasonsContainer>
            {isSignalConfigsLoading ? (
              <LoadingWrapper>
                <Loading size="large" />
              </LoadingWrapper>
            ) : hasNoSignalConfigurations ? (
              <SignalsV2AccountEmptyState
                kind="no_configs"
                canCreateSignal={canCreateSignal}
              />
            ) : isLoading ? (
              <LoadingWrapper>
                <Loading size="large" />
              </LoadingWrapper>
            ) : allSignalsHaveNoReasonData ? (
              <SignalsV2AccountEmptyState
                kind="no_signals"
                canCreateSignal={false}
              />
            ) : (
              mappedSignals.map((signal) => (
                <SignalSection
                  key={signal.signalConfigurationId}
                  id={`signal-section-${signal.signalConfigurationId}`}
                >
                  <SignalHeader>
                    <SignalIcon
                      icon={getSignalIcon(signal.signalType)}
                      $signalType={signal.signalType}
                      size={16}
                    />
                    <SignalName>{signal.signalName}</SignalName>
                  </SignalHeader>

                  {signal.reasonGroups.length === 0 ? (
                    <NoSignalFound>
                      {translate(
                        'accounts.page.signals_v2.reasons.no_signal_found',
                      )}
                    </NoSignalFound>
                  ) : (
                    signal.reasonGroups.map((reasonGroup) => (
                      <ReasonGroup key={reasonGroup.reasonId}>
                        <ReasonTag
                          variant="basic"
                          $signalType={signal.signalType}
                        >
                          {reasonGroup.reasonLabel}
                        </ReasonTag>
                        <ReasonList>
                          {reasonGroup.reasons.map((reason, index) => (
                            <ReasonItem
                              key={`${reasonGroup.reasonId}-${index}`}
                            >
                              {reason}
                            </ReasonItem>
                          ))}
                        </ReasonList>
                      </ReasonGroup>
                    ))
                  )}
                </SignalSection>
              ))
            )}
          </ReasonsContainer>
        </ReasonsContent>
      </ReasonsWrapper>
    </>
  );
};
